import { Injectable } from '@angular/core';

import pptxgen from 'pptxgenjs';
import { GeneratedPptContentSlide } from '../models/wizard.models';

// Theme
const THEME = {
  primary: '1e3a5f',
  accent: 'c0392b',
  accentLight: 'e74c3c',
  white: 'FFFFFF',
  textDark: '1f2937',
  textMid: '4b5563',
  textLight: '9ca3af',
  bgSlide: 'f8fafc',
  bgModule: '1e3a5f',
  bgArtifact: 'f0f4f8',
  border: 'e2e8f0',
};

// LAYOUT_WIDE = 13.33 x 7.5
const W = 13.33;
const H = 7.5;
const MARGIN = 0.55;

@Injectable({ providedIn: 'root' })
export class PptBuilderService {
  /**
   * Builds a .pptx from generated slide content and triggers browser download.
   */
  async downloadPresentation(
    slides: GeneratedPptContentSlide[],
    filename = 'crisis-exercise',
  ): Promise<void> {
    const pptx = new pptxgen();
    pptx.layout = 'LAYOUT_WIDE';
    pptx.author = 'Crisis Exercise Tool';
    pptx.subject = 'Crisis Tabletop Exercise';

    for (const slideData of slides) {
      const slide = pptx.addSlide();
      this.renderSlide(slide, slideData);
    }

    await pptx.writeFile({ fileName: `${filename}.pptx` });
  }

  private renderSlide(
    slide: pptxgen.Slide,
    data: GeneratedPptContentSlide,
  ): void {
    const c = data.content as Record<string, unknown>;

    if (data.speakerNotes) {
      slide.addNotes(data.speakerNotes);
    }

    switch (data.layout) {
      case 'title':
        this.renderTitleSlide(slide, c);
        break;
      case 'module-intro':
        this.renderModuleIntroSlide(slide, data.title, c);
        break;
      case 'two-column':
        this.renderTwoColumnSlide(slide, data.title, c);
        break;
      case 'timeline':
        this.renderTimelineSlide(slide, data.title, c);
        break;
      case 'questions':
        this.renderQuestionsSlide(slide, data.title, c);
        break;
      case 'aar':
        this.renderAarSlide(slide, data.title, c);
        break;
      case 'summary':
        this.renderBulletSlide(slide, data.title, c, true);
        break;
      case 'news-article':
        this.renderArtifactSlide(slide, data.title, c, 'NEWS ARTICLE');
        break;
      case 'email-inject':
        this.renderArtifactSlide(slide, data.title, c, 'EMAIL INJECT');
        break;
      case 'social-media':
        this.renderArtifactSlide(slide, data.title, c, 'SOCIAL MEDIA');
        break;
      default:
        this.renderBulletSlide(slide, data.title, c, false);
    }
  }

  private renderTitleSlide(
    slide: pptxgen.Slide,
    c: Record<string, unknown>,
  ): void {
    slide.addShape(pptxgen.ShapeType.rect, {
      x: 0,
      y: 0,
      w: W,
      h: H,
      fill: { color: THEME.primary },
    });

    slide.addShape(pptxgen.ShapeType.rect, {
      x: 0,
      y: H - 0.5,
      w: W,
      h: 0.5,
      fill: { color: THEME.accent },
    });

    const headline =
      typeof c['headline'] === 'string' ? c['headline'] : 'Crisis Exercise';

    slide.addText(headline, {
      x: 1,
      y: 2.2,
      w: W - 2,
      h: 3,
      color: THEME.white,
      fontSize: 40,
      bold: true,
      align: 'center',
      valign: 'middle',
    });
  }

  private renderModuleIntroSlide(
    slide: pptxgen.Slide,
    title: string,
    c: Record<string, unknown>,
  ): void {
    slide.addShape(pptxgen.ShapeType.rect, {
      x: 0,
      y: 0,
      w: W,
      h: H,
      fill: { color: THEME.bgModule },
    });

    slide.addShape(pptxgen.ShapeType.rect, {
      x: 0,
      y: 0,
      w: 0.2,
      h: H,
      fill: { color: THEME.accent },
    });

    const moduleLabel =
      c['moduleNumber'] !== undefined ? `MODULE ${c['moduleNumber']}` : '';
    const timescale = typeof c['timescale'] === 'string' ? c['timescale'] : '';
    const summary = typeof c['summary'] === 'string' ? c['summary'] : '';

    if (moduleLabel) {
      slide.addText(moduleLabel, {
        x: 0.55,
        y: 0.6,
        w: W - 1,
        h: 0.5,
        color: THEME.accentLight,
        fontSize: 14,
        bold: true,
        fontFace: 'Calibri',
      });
    }

    slide.addText(title, {
      x: 0.55,
      y: 1.3,
      w: W - 1,
      h: 1.5,
      color: THEME.white,
      fontSize: 32,
      bold: true,
    });

    if (timescale) {
      slide.addText(timescale, {
        x: 0.55,
        y: 3.0,
        w: 3,
        h: 0.45,
        color: THEME.white,
        fontSize: 13,
        bold: true,
        fill: { color: THEME.accent },
        align: 'center',
        valign: 'middle',
      });
    }

    if (summary) {
      slide.addText(summary, {
        x: 0.55,
        y: 3.7,
        w: W - 1.1,
        h: 2.5,
        color: THEME.textLight,
        fontSize: 15,
        italic: true,
      });
    }
  }

  private renderBulletSlide(
    slide: pptxgen.Slide,
    title: string,
    c: Record<string, unknown>,
    isSummary: boolean,
  ): void {
    this.addSlideHeader(slide, title, isSummary ? THEME.accent : THEME.primary);

    const bullets = Array.isArray(c['bullets'])
      ? (c['bullets'] as unknown[]).map((b) => String(b))
      : this.extractFallbackBullets(c);

    if (bullets.length > 0) {
      const rows = bullets.map((b) => ({
        text: b,
        options: {
          bullet: { type: 'bullet' as const },
          fontSize: 17,
          color: THEME.textDark,
          paraSpaceAfter: 6,
        },
      }));

      slide.addText(rows, {
        x: MARGIN,
        y: 1.6,
        w: W - MARGIN * 2,
        h: H - 2.1,
        valign: 'top',
      });
    }
  }

  private renderTwoColumnSlide(
    slide: pptxgen.Slide,
    title: string,
    c: Record<string, unknown>,
  ): void {
    this.addSlideHeader(slide, title, THEME.primary);

    const leftItems = Array.isArray(c['leftColumn'])
      ? (c['leftColumn'] as unknown[]).map((b) => String(b))
      : [];
    const rightItems = Array.isArray(c['rightColumn'])
      ? (c['rightColumn'] as unknown[]).map((b) => String(b))
      : [];

    const colH = H - 2.1;
    const colW = (W - MARGIN * 3) / 2;

    if (leftItems.length > 0) {
      slide.addText(
        leftItems.map((b) => ({
          text: b,
          options: {
            bullet: { type: 'bullet' as const },
            fontSize: 15,
            color: THEME.textDark,
            paraSpaceAfter: 8,
          },
        })),
        { x: MARGIN, y: 1.6, w: colW, h: colH, valign: 'top' },
      );
    }

    slide.addShape(pptxgen.ShapeType.line, {
      x: MARGIN + colW + 0.15,
      y: 1.7,
      w: 0,
      h: colH - 0.2,
      line: { color: THEME.border, width: 1 },
    });

    if (rightItems.length > 0) {
      slide.addText(
        rightItems.map((b) => ({
          text: b,
          options: {
            bullet: { type: 'bullet' as const },
            fontSize: 15,
            color: THEME.textDark,
            paraSpaceAfter: 8,
          },
        })),
        { x: MARGIN + colW + 0.35, y: 1.6, w: colW, h: colH, valign: 'top' },
      );
    }
  }

  private renderTimelineSlide(
    slide: pptxgen.Slide,
    title: string,
    c: Record<string, unknown>,
  ): void {
    this.addSlideHeader(slide, title, THEME.primary);

    const items = Array.isArray(c['timelineItems'])
      ? (c['timelineItems'] as unknown[])
      : [];

    const normalized = items
      .map((item) => item as { time?: unknown; event?: unknown })
      .map((item) => ({
        time: typeof item.time === 'string' ? item.time : 'Time',
        event: typeof item.event === 'string' ? item.event : '',
      }))
      .filter((item) => item.event.length > 0);

    let y = 1.7;
    for (const row of normalized) {
      slide.addShape(pptxgen.ShapeType.roundRect, {
        x: MARGIN,
        y,
        w: 1.75,
        h: 0.42,
        fill: { color: THEME.accent },
        line: { color: THEME.accent },
      });

      slide.addText(row.time, {
        x: MARGIN,
        y: y + 0.03,
        w: 1.75,
        h: 0.36,
        align: 'center',
        valign: 'middle',
        color: THEME.white,
        bold: true,
        fontSize: 12,
      });

      slide.addText(row.event, {
        x: MARGIN + 2.05,
        y: y,
        w: W - (MARGIN + 2.05) - MARGIN,
        h: 0.5,
        fontSize: 14,
        color: THEME.textDark,
      });

      y += 0.82;
      if (y > H - 0.9) {
        break;
      }
    }
  }

  private renderQuestionsSlide(
    slide: pptxgen.Slide,
    title: string,
    c: Record<string, unknown>,
  ): void {
    this.addSlideHeader(slide, title, THEME.accent);

    const bullets = Array.isArray(c['bullets'])
      ? (c['bullets'] as unknown[]).map((b) => String(b))
      : [];

    const rows = bullets.map((b) => ({
      text: b,
      options: {
        bullet: { type: 'bullet' as const },
        fontSize: 18,
        color: THEME.textDark,
        bold: true,
        paraSpaceAfter: 10,
      },
    }));

    if (rows.length > 0) {
      slide.addText(rows, {
        x: MARGIN,
        y: 1.6,
        w: W - MARGIN * 2,
        h: H - 2.1,
        valign: 'top',
      });
    }
  }

  private renderAarSlide(
    slide: pptxgen.Slide,
    title: string,
    c: Record<string, unknown>,
  ): void {
    this.addSlideHeader(slide, title, THEME.primary);

    const labels = [
      'What Went Well',
      'What Did Not Go Well',
      'Actions & Owners',
    ];
    const sections = [c['well'], c['gaps'], c['actions']];

    const boxW = (W - MARGIN * 2 - 0.6) / 3;
    const y = 1.6;
    const h = H - 2.1;

    for (let i = 0; i < 3; i += 1) {
      const x = MARGIN + i * (boxW + 0.3);
      slide.addShape(pptxgen.ShapeType.roundRect, {
        x,
        y,
        w: boxW,
        h,
        fill: { color: THEME.bgArtifact },
        line: { color: THEME.border, width: 1 },
      });

      slide.addText(labels[i], {
        x: x + 0.15,
        y: y + 0.12,
        w: boxW - 0.3,
        h: 0.35,
        fontSize: 12,
        bold: true,
        color: THEME.primary,
      });

      const bullets = Array.isArray(sections[i])
        ? (sections[i] as unknown[]).map((v) => String(v))
        : [];

      slide.addText(
        bullets.map((b) => ({
          text: b,
          options: {
            bullet: { type: 'bullet' as const },
            fontSize: 11,
            color: THEME.textDark,
            paraSpaceAfter: 5,
          },
        })),
        {
          x: x + 0.15,
          y: y + 0.55,
          w: boxW - 0.3,
          h: h - 0.7,
          valign: 'top',
        },
      );
    }
  }

  private renderArtifactSlide(
    slide: pptxgen.Slide,
    title: string,
    c: Record<string, unknown>,
    tag: string,
  ): void {
    this.addSlideHeader(slide, title, THEME.primary);

    slide.addShape(pptxgen.ShapeType.roundRect, {
      x: MARGIN,
      y: 1.65,
      w: W - MARGIN * 2,
      h: H - 2.15,
      fill: { color: THEME.bgArtifact },
      line: { color: THEME.border, width: 1 },
    });

    slide.addShape(pptxgen.ShapeType.roundRect, {
      x: MARGIN + 0.2,
      y: 1.85,
      w: 2.1,
      h: 0.38,
      fill: { color: THEME.accent },
      line: { color: THEME.accent },
    });

    slide.addText(tag, {
      x: MARGIN + 0.2,
      y: 1.89,
      w: 2.1,
      h: 0.3,
      align: 'center',
      valign: 'middle',
      color: THEME.white,
      fontSize: 11,
      bold: true,
    });

    const body = this.extractFallbackBullets(c).join('\n\n');
    slide.addText(body, {
      x: MARGIN + 0.28,
      y: 2.35,
      w: W - MARGIN * 2 - 0.56,
      h: H - 2.9,
      fontSize: 13,
      color: THEME.textDark,
      valign: 'top',
      breakLine: true,
    });
  }

  private addSlideHeader(
    slide: pptxgen.Slide,
    title: string,
    color: string,
  ): void {
    slide.addShape(pptxgen.ShapeType.rect, {
      x: 0,
      y: 0,
      w: W,
      h: H,
      fill: { color: THEME.bgSlide },
      line: { color: THEME.bgSlide },
    });

    slide.addShape(pptxgen.ShapeType.rect, {
      x: 0,
      y: 0,
      w: W,
      h: 0.16,
      fill: { color },
      line: { color },
    });

    slide.addText(title || 'Untitled Slide', {
      x: MARGIN,
      y: 0.42,
      w: W - MARGIN * 2,
      h: 0.65,
      color: THEME.textDark,
      fontSize: 25,
      bold: true,
    });

    slide.addShape(pptxgen.ShapeType.line, {
      x: MARGIN,
      y: 1.2,
      w: W - MARGIN * 2,
      h: 0,
      line: { color: THEME.border, width: 1 },
    });
  }

  private extractFallbackBullets(c: Record<string, unknown>): string[] {
    const out: string[] = [];

    for (const [key, value] of Object.entries(c)) {
      if (value === null || value === undefined) {
        continue;
      }

      if (Array.isArray(value)) {
        for (const item of value) {
          out.push(typeof item === 'string' ? item : JSON.stringify(item));
        }
        continue;
      }

      if (typeof value === 'string') {
        out.push(`${this.toTitleCase(key)}: ${value}`);
        continue;
      }

      if (typeof value === 'number' || typeof value === 'boolean') {
        out.push(`${this.toTitleCase(key)}: ${String(value)}`);
        continue;
      }

      out.push(`${this.toTitleCase(key)}: ${JSON.stringify(value)}`);
    }

    return out;
  }

  private toTitleCase(input: string): string {
    return input
      .replace(/[-_]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\b\w/g, (m) => m.toUpperCase());
  }
}
